import { Injectable, computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  StartUploadConfig,
  UploadTask,
  UploadTaskStatus,
} from './upload-tracker.models';

@Injectable({ providedIn: 'root' })
export class UploadTrackerService {
  readonly tasks = signal<UploadTask[]>([]);

  readonly hasVisibleTasks = computed(() => this.tasks().length > 0);

  readonly globalProgress = computed(() => {
    const all = this.tasks();
    let completed = 0;
    let total = 0;
    for (const t of all) {
      if (t.status === 'running' || t.status === 'completed') {
        completed += t.completedFiles;
        total += t.totalFiles;
      }
    }
    return { completed, total };
  });

  private readonly subscriptions = new Map<string, Subscription>();
  private readonly configs = new Map<string, StartUploadConfig>();
  private idCounter = 0;

  startUpload(config: StartUploadConfig): void {
    const id = `ul_${++this.idCounter}_${Date.now()}`;

    const task: UploadTask = {
      id,
      label: config.label,
      files: config.files,
      totalFiles: config.files.length,
      completedFiles: 0,
      currentFileIndex: 0,
      status: 'running',
      errors: [],
    };

    this.configs.set(id, config);
    this.tasks.update(list => [...list, task]);
    this.processNextFile(id);
  }

  cancelTask(id: string): void {
    this.subscriptions.get(id)?.unsubscribe();
    this.subscriptions.delete(id);
    this.updateTask(id, t => ({ ...t, status: 'cancelled' as UploadTaskStatus }));
  }

  retryTask(id: string): void {
    const task = this.tasks().find(t => t.id === id);
    if (!task) return;

    this.updateTask(id, t => ({
      ...t,
      status: 'running' as UploadTaskStatus,
      errors: [],
    }));

    this.processNextFile(id);
  }

  dismissTask(id: string): void {
    this.subscriptions.get(id)?.unsubscribe();
    this.subscriptions.delete(id);
    this.configs.delete(id);
    this.tasks.update(list => list.filter(t => t.id !== id));
  }

  private processNextFile(taskId: string): void {
    const task = this.tasks().find(t => t.id === taskId);
    if (!task || task.status === 'cancelled') return;

    const config = this.configs.get(taskId);
    if (!config) return;

    if (task.currentFileIndex >= task.totalFiles) {
      this.updateTask(taskId, t => ({ ...t, status: 'completed' }));
      return;
    }

    const file = task.files[task.currentFileIndex];

    const sub = config.uploadFn(file).subscribe({
      next: () => {
        this.updateTask(taskId, t => ({
          ...t,
          completedFiles: t.completedFiles + 1,
          currentFileIndex: t.currentFileIndex + 1,
        }));
        this.processNextFile(taskId);
      },
      error: (err) => {
        this.updateTask(taskId, t => ({
          ...t,
          status: 'failed',
          errors: [...t.errors, err?.message ?? `${file.name} yüklenemedi.`],
        }));
      },
    });

    this.subscriptions.set(taskId, sub);
  }

  private updateTask(id: string, updater: (task: UploadTask) => UploadTask): void {
    this.tasks.update(list =>
      list.map(t => (t.id === id ? updater(t) : t)),
    );
  }
}
